import React, { useEffect, useRef, useState } from "react";
import { soundApi, money } from "../soundDetail/soundApi";

// "Add a sound" for a new post: browse or search the sound library and pick the sound the video uses. The post is then saved with
// that sound's id, so it appears under "Videos using this sound" on the sound's page. `value` is { id, title, artist_name } or null.
// The library is licence-aware: a creator or business account sees whether each sound is theirs, already licensed, free to use or
// needs a licence first. A sound that needs a licence cannot be picked here (the post would be refused); it links to the sound page
// where the licence is bought.
export function licenceNote(licence) {
  switch (licence && licence.state) {
    case "own": return { text: "Your sound", tone: "ok" };
    case "held": return { text: "Licence held", tone: "ok" };
    case "free": return { text: "Free licence", tone: "ok" };
    case "paid": return { text: licence.from_price ? `Licence from ${money(licence.from_price)}` : "Licence needed", tone: "warn" };
    default: return null;
  }
}

export default function SoundPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [state, setState] = useState({ items: [], loading: false, error: "", more: false });
  const timer = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    clearTimeout(timer.current);
    let current = true;
    const run = () => {
      setState((s) => ({ ...s, loading: true, error: "", ...(page === 1 ? { items: [] } : {}) }));
      soundApi.composerLibrary({ q: query.trim() || undefined, page, limit: 20 })
        .then((data) => current && setState((s) => ({
          items: page === 1 ? (data?.items || []) : [...s.items, ...(data?.items || [])],
          loading: false, error: "", more: Boolean(data?.has_more)
        })))
        .catch(() => current && setState((s) => ({ items: page === 1 ? [] : s.items, loading: false, error: "We couldn't load sounds. Please try again.", more: false })));
    };
    // Typing waits a moment; opening the list and paging load straight away.
    if (query.trim() && page === 1) timer.current = setTimeout(run, 300); else run();
    return () => { current = false; clearTimeout(timer.current); };
  }, [open, query, page]);

  const pick = (sound) => {
    onChange({ id: sound.id, title: sound.title, artist_name: sound.artist_name });
    setOpen(false);
    setQuery("");
    setPage(1);
  };

  if (value) {
    return (
      <div className="mt-3 flex items-center gap-2 text-sm" data-testid="selected-sound">
        <span className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-gray-800">
          <span aria-hidden="true">♪</span>
          <span className="font-semibold">{value.title}</span>
          {value.artist_name && <span className="text-gray-500">· {value.artist_name}</span>}
          <button type="button" aria-label="Remove sound" className="text-gray-500 hover:text-gray-900" onClick={() => onChange(null)}>×</button>
        </span>
      </div>
    );
  }

  return (
    <div className="mt-3 text-sm">
      <button type="button" onClick={() => { setOpen((o) => !o); setPage(1); }} className="font-semibold underline" style={{ color: "#095228" }} data-testid="add-sound">
        {open ? "Close sound library" : "♪ Add a sound"}
      </button>
      {open && (
        <div className="mt-2 rounded-xl border border-gray-200 p-3">
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
            placeholder="Search sounds or artists"
            aria-label="Search sounds"
            maxLength={100}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:outline-none"
            autoFocus
          />
          <div className="mt-2 max-h-56 overflow-y-auto" role="listbox" aria-label="Sounds">
            {state.items.map((s) => {
              const note = licenceNote(s.licence);
              const needsLicence = s.licence && s.licence.state === "paid";
              const label = (
                <span className="min-w-0">
                  <span className="block truncate font-semibold text-gray-900">{s.title}</span>
                  <span className="block truncate text-gray-500">{s.artist_name}</span>
                </span>
              );
              const badge = note && (
                <span data-testid={`licence-note-${s.id}`} className={`shrink-0 text-xs font-semibold ${note.tone === "warn" ? "text-amber-700" : "text-green-800"}`}>{note.text}</span>
              );
              if (needsLicence) {
                return (
                  <div key={s.id} role="option" aria-selected="false" aria-disabled="true" className="flex w-full items-center justify-between gap-3 rounded-lg px-2 py-2">
                    {label}
                    <span className="flex shrink-0 items-center gap-3">
                      {badge}
                      <a href={`/sound/${s.id}`} target="_blank" rel="noreferrer" className="text-xs font-semibold underline" style={{ color: "#095228" }}>Get licence</a>
                    </span>
                  </div>
                );
              }
              return (
                <button type="button" key={s.id} role="option" aria-selected="false" onClick={() => pick(s)} className="flex w-full items-center justify-between gap-3 rounded-lg px-2 py-2 text-left hover:bg-gray-50">
                  {label}
                  {badge}
                </button>
              );
            })}
            {state.loading && <p className="px-1 py-2 text-gray-500">{page > 1 ? "Loading more…" : query.trim() ? "Searching…" : "Loading sounds…"}</p>}
            {state.error && <p className="px-1 py-2 text-red-600">{state.error}</p>}
            {!state.loading && !state.error && state.items.length === 0 && (
              <p className="px-1 py-2 text-gray-500">{query.trim() ? "No sounds match your search." : "No sounds are available to use yet."}</p>
            )}
            {!state.loading && state.more && (
              <button type="button" onClick={() => setPage((n) => n + 1)} className="w-full px-2 py-2 text-center text-xs font-semibold underline" style={{ color: "#095228" }}>Show more</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
